import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'

type Sourceable = components['schemas']['Sourceable']

/**
 * Which of the two independently-served catalogs a resolved part number came
 * from. MISUMI/partner rows come from the Catalog & Discovery service
 * (`/catalog/search`, `/catalog/configurable/{sku}`); Fictiv custom parts come
 * from the tenant Parts Library (`/parts`). The two are SEPARATE backends — no
 * single endpoint federates them — so this resolver queries both in parallel
 * and merges (see {@link resolveAcrossSources}).
 */
export type SkuSource = 'catalog' | 'library'

/**
 * The outcome of resolving one typed/pasted part number across both sources.
 * Part numbers are unique, so a hit is a DIRECT add — never a pick-from-list.
 */
export type SkuResolveOutcome =
  | {
      readonly status: 'resolved'
      readonly sourceable: Sourceable
      readonly source: SkuSource
      /** Both sources returned a hit for this (supposedly unique) number — the
       * exact-SKU catalog match was preferred (see the merge rules). */
      readonly collision: boolean
      /** Sources whose lookup errored while the other still answered — the UI
       * shows the answer plus a "couldn't check X" note. */
      readonly failedSources: readonly SkuSource[]
    }
  | {
      /** A catalog *configurable family* row (needs the configurator to price):
       * do NOT add it blindly — mirror `ResultCard`'s "Configure" affordance. */
      readonly status: 'needs-config'
      readonly sourceable: Sourceable
      readonly source: 'catalog'
      readonly failedSources: readonly SkuSource[]
    }
  | { readonly status: 'not-found'; readonly failedSources: readonly SkuSource[] }
  | { readonly status: 'error'; readonly message: string }

/**
 * The data-layer seam for the empty-cart "add by part number" flow (single
 * add-line box, CSV upload, and CSV paste popup all go through ONE resolver).
 * Injectable exactly like `useCatalogSearch`'s `CatalogSearchService` /
 * `useConfigurableResolve`'s `ConfigurableResolveService`: the default is the
 * real BFF-backed implementation; tests inject a fake.
 */
export interface SkuResolveService {
  resolve(partNumber: string): Promise<SkuResolveOutcome>
}

/** One source's answer: the resolved Sourceable + whether it still needs
 * configuration before it can be priced/added. `null` = no hit from that
 * source. A thrown error = that source failed (kept distinct from "no hit"). */
interface SourceHit {
  readonly sourceable: Sourceable
  readonly needsConfig: boolean
}

/** Pull the `title` off a Problem+JSON error body for a user-facing message —
 * the same mapping `useCatalogSearch`/`useConfigurableResolve` use. */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

/** Case-insensitive, whitespace-trimmed equality against a typed part number. */
function matchesNumber(candidate: string | null | undefined, partNumber: string): boolean {
  return (
    typeof candidate === 'string' && candidate.trim().toLowerCase() === partNumber.toLowerCase()
  )
}

/** "Looks like a full SKU" heuristic (the plan's exact-resolve fast-path gate):
 * a single whitespace-free token. A multi-word query is a keyword, not a SKU. */
function looksLikeSku(partNumber: string): boolean {
  return partNumber.length > 0 && !/\s/.test(partNumber)
}

/**
 * MISUMI/partner side. Prefer the exact resolve (`GET /catalog/configurable/
 * {sku}`) when the string looks like a full SKU — a composed literal SKU
 * resolves to a concrete, already-priced point, so it is directly addable.
 * Otherwise (or on a 404 from the resolve) fall back to `GET /catalog/search`
 * and take an EXACT `catalogSku` match. A matched *family* row (carries a
 * `configSchema`, or `kind === 'configurable'`) still needs configuring.
 */
async function catalogLookup(partNumber: string): Promise<SourceHit | null> {
  if (looksLikeSku(partNumber)) {
    // Best-effort exact resolve. The configurable endpoint is MISUMI-only, so
    // for a Fictiv id the demo server has no route and falls through to serving
    // the SPA shell (a 200 `text/html` index.html, or a 304) — NOT JSON and NOT
    // a clean 404. Treat anything that isn't a well-formed JSON resolution as a
    // clean MISS and fall through to keyword search + the parallel `/parts`
    // lookup — never a hard error, and never parse an HTML shell as a part.
    const { data } = await apiClient.GET('/catalog/configurable/{sku}', {
      params: { path: { sku: partNumber } },
    })
    const resolved = configurableSourceable(data)
    if (resolved) {
      return { sourceable: resolved, needsConfig: false }
    }
  }

  const { data, error } = await apiClient.GET('/catalog/search', {
    params: { query: { q: partNumber } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t search the catalog.'))
  }
  const rawItems = (data as { items?: readonly CatalogCardish[] }).items
  const items = Array.isArray(rawItems) ? rawItems : []
  const match = items.find((item) => matchesNumber(item.sourceable.catalogSku, partNumber))
  if (!match) return null
  const needsConfig = match.configSchema != null || match.sourceable.kind === 'configurable'
  return { sourceable: match.sourceable, needsConfig }
}

/**
 * A configurable-resolution response is trustworthy ONLY when it's a real JSON
 * object carrying a `sourceable` object with a string `id`. An HTML/SPA body
 * (the MISUMI-only endpoint's fall-through for a Fictiv id), a 304, or any
 * non-JSON payload deserializes to a string/undefined and is rejected here — a
 * MISS, never a bad "match" and never a thrown error that would block the
 * parallel `/parts` lookup from resolving the Fictiv part.
 */
function configurableSourceable(data: unknown): Sourceable | undefined {
  if (typeof data !== 'object' || data === null) return undefined
  const sourceable = (data as { sourceable?: unknown }).sourceable
  if (typeof sourceable !== 'object' || sourceable === null) return undefined
  if (typeof (sourceable as { id?: unknown }).id !== 'string') return undefined
  return sourceable as Sourceable
}

interface CatalogCardish {
  readonly sourceable: Sourceable
  readonly configSchema?: unknown
}

interface LibraryRowish {
  readonly sourceable: Sourceable
}

/**
 * Fictiv custom side. NOTE (verified against the API: parts-library
 * `library-store.ts` `matchesQuery`): `GET /parts?q=` filters on part NAME +
 * drawing filename only — NOT `catalogSku`/part number. So we fetch the
 * tenant's (unpaginated) library listing and match the part number EXACTLY
 * client-side against `catalogSku`, then the Sourceable `id` (a Fictiv CUSTOM
 * part has no `catalogSku` — its `id`, e.g. `…-p008`, IS its part number), then
 * an exact `name` match.
 */
async function libraryLookup(partNumber: string): Promise<SourceHit | null> {
  const { data, error } = await apiClient.GET('/parts', { params: { query: {} } })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t search your parts library.'))
  }
  const rows = (data as { parts?: readonly LibraryRowish[] }).parts ?? []
  const match =
    rows.find((row) => matchesNumber(row.sourceable.catalogSku, partNumber)) ??
    rows.find((row) => matchesNumber(row.sourceable.id, partNumber)) ??
    rows.find((row) => matchesNumber(row.sourceable.name, partNumber))
  if (!match) return null
  return { sourceable: match.sourceable, needsConfig: match.sourceable.kind === 'configurable' }
}

function reasonMessage(reason: unknown): string {
  return reason instanceof Error && reason.message ? reason.message : ''
}

/**
 * Resolve a part number across BOTH sources in parallel and merge into one
 * outcome. Part numbers are unique, so at most one source normally hits; if
 * both hit, the exact-SKU catalog match wins and `collision` is flagged. If one
 * source errors, the other's answer still stands (with a `failedSources` note);
 * only a double failure is a hard `error`.
 */
export async function resolveAcrossSources(partNumber: string): Promise<SkuResolveOutcome> {
  const trimmed = partNumber.trim()
  if (trimmed === '') return { status: 'not-found', failedSources: [] }

  const [catalogSettled, librarySettled] = await Promise.allSettled([
    catalogLookup(trimmed),
    libraryLookup(trimmed),
  ])

  const failedSources: SkuSource[] = []
  const messages: string[] = []
  let catalogHit: SourceHit | null = null
  let libraryHit: SourceHit | null = null

  if (catalogSettled.status === 'fulfilled') {
    catalogHit = catalogSettled.value
  } else {
    failedSources.push('catalog')
    messages.push(reasonMessage(catalogSettled.reason))
  }
  if (librarySettled.status === 'fulfilled') {
    libraryHit = librarySettled.value
  } else {
    failedSources.push('library')
    messages.push(reasonMessage(librarySettled.reason))
  }

  const collision = Boolean(catalogHit && libraryHit)

  // Prefer a directly-addable exact-SKU catalog match (also the collision tie-break).
  if (catalogHit && !catalogHit.needsConfig) {
    return {
      status: 'resolved',
      sourceable: catalogHit.sourceable,
      source: 'catalog',
      collision,
      failedSources,
    }
  }
  if (libraryHit) {
    return {
      status: 'resolved',
      sourceable: libraryHit.sourceable,
      source: 'library',
      collision,
      failedSources,
    }
  }
  // The only remaining catalog signal is a configurable family → needs configuring.
  if (catalogHit?.needsConfig) {
    return {
      status: 'needs-config',
      sourceable: catalogHit.sourceable,
      source: 'catalog',
      failedSources,
    }
  }
  // No hit from either source.
  if (failedSources.length === 2) {
    return {
      status: 'error',
      message: messages.filter(Boolean).join('; ') || 'Both catalog and library lookups failed.',
    }
  }
  return { status: 'not-found', failedSources }
}

/**
 * The REAL BFF-backed resolver the running app uses. Queries the Catalog &
 * Discovery service and the tenant Parts Library through the shared
 * `apiClient`; tests inject their own `SkuResolveService`.
 */
export const defaultSkuResolveService: SkuResolveService = { resolve: resolveAcrossSources }
